// Room Move — pick a free physical unit for an in-house / reserved guest.
import React, { useEffect, useState } from 'react';
import { ArrowRightLeft, Loader2, X } from 'lucide-react';
import { apiCall } from './_shared';

const STATUS_CLS = {
  clean: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  inspected: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  cleaning: 'border-amber-300 bg-amber-50 text-amber-800',
  dirty: 'border-red-300 bg-red-50 text-red-800',
  to_repair: 'border-slate-300 bg-slate-100 text-slate-500',
  ooo: 'border-slate-300 bg-slate-100 text-slate-500',
  occupied: 'border-sky-300 bg-sky-50 text-sky-800',
};

const RoomMoveModal = ({ bookingId, guestName, onClose, onMoved }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [needForce, setNeedForce] = useState(false);

  useEffect(() => {
    apiCall(`/api/admin/reception/folios/${bookingId}/move-options`).then(setData).catch((e) => setErr(e.message));
  }, [bookingId]);

  const move = async (force = false) => {
    if (!sel) return;
    setBusy(true); setErr(''); setNeedForce(false);
    try {
      const f = await apiCall(`/api/admin/reception/folios/${bookingId}/move`, {
        method: 'POST', body: JSON.stringify({ room_id: sel.room_id, unit_id: sel.unit_id, reason, force }),
      });
      onMoved?.(f);
      onClose();
    } catch (e) {
      setErr(e.message);
      if (/force/i.test(e.message)) setNeedForce(true);
    } finally { setBusy(false); }
  };

  const groups = (data?.rooms || []).map((r) => {
    const chips = r.units.map((u) => {
      const selected = sel?.unit_key === u.unit_key;
      const cls = u.current ? 'border-orange-400 bg-orange-100 text-orange-800 ring-2 ring-orange-300'
        : selected ? 'border-slate-900 bg-slate-900 text-white'
        : u.occupied_by ? 'border-slate-200 bg-slate-50 text-slate-400 line-through'
        : STATUS_CLS[u.status] || STATUS_CLS.clean;
      const title = u.current ? 'Current room'
        : u.occupied_by ? `Occupied: ${u.occupied_by.guest_name} (${u.occupied_by.check_in} → ${u.occupied_by.check_out})`
        : `${u.status.replace('_', ' ')}`;
      return (
        <button
          key={u.unit_key}
          disabled={!!u.occupied_by || u.current || ['to_repair', 'ooo'].includes(u.status)}
          onClick={() => setSel({ ...u, room_id: r.room_id, room_name: r.room_name })}
          title={title}
          className={`px-2 py-1 rounded-md border text-xs font-semibold disabled:cursor-not-allowed transition-colors ${cls}`}
          data-testid={`move-unit-${u.unit_key}`}
        >
          {u.label}
        </button>
      );
    });
    return (
      <div key={r.room_id} className="py-2 border-b last:border-0">
        <p className="text-xs font-semibold text-slate-700 mb-1">{r.room_name}</p>
        <div className="flex flex-wrap gap-1.5">{chips}</div>
      </div>
    );
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="room-move-modal">
        <div className="p-4 bg-slate-900 text-white flex justify-between items-center">
          <div>
            <h3 className="font-bold flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-orange-400" /> Move {guestName}</h3>
            <p className="text-xs text-slate-300">
              {data?.current?.room_name ? `Currently ${data.current.room_name}${data.current.unit_id ? ` · unit ${data.current.unit_id}` : ''}` : 'Current room unknown'}
              {data?.window && ` · free for ${data.window.start} → ${data.window.end}`}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="room-move-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {!data && !err && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>}
          {data && (
            <>
              <div className="flex flex-wrap gap-3 text-[11px] text-slate-500 mb-2">
                <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-200 border border-emerald-400 mr-1" />clean</span>
                <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-200 border border-red-400 mr-1" />dirty</span>
                <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-200 border border-amber-400 mr-1" />cleaning</span>
                <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-100 border border-slate-300 mr-1" />occupied / out of order</span>
                <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-orange-200 border border-orange-400 mr-1" />current</span>
              </div>
              {groups}
            </>
          )}
        </div>
        <div className="p-4 border-t bg-slate-50 space-y-2">
          <div className="flex gap-2 items-center">
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (noise, AC broken, upgrade…)" className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="room-move-reason" />
            <button onClick={() => move(false)} disabled={!sel || busy} className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="room-move-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
              {sel ? `Move to ${sel.label}` : 'Pick a room'}
            </button>
          </div>
          {err && (
            <div className="text-xs text-red-600 flex items-center justify-between gap-2" data-testid="room-move-error">
              <span>{err}</span>
              {needForce && <button onClick={() => move(true)} disabled={busy} className="px-2 py-1 rounded border border-red-300 text-red-700 font-semibold hover:bg-red-50" data-testid="room-move-force">Force move</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoomMoveModal;
